import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { rankAboveAPI } from '../services/apiEndpoints';
import { TREND_LOCATIONS, DEFAULT_LOCATION } from '../data/trendLocations';
import QuickPost from './QuickPost';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
    MagnifyingGlassIcon,
    RocketIcon,
    TargetIcon,
    LightningBoltIcon,
    CheckCircledIcon,
    CrossCircledIcon,
    PlusIcon,
    ReloadIcon,
    ClockIcon,
    ArrowUpIcon,
    ExclamationTriangleIcon,
    BarChartIcon,
    StarIcon,
    ChevronRightIcon,
} from '@radix-ui/react-icons';

// ─── helpers ─────────────────────────────────────────────────────────────────

const stripDomain = (url = '') => {
    try {
        return new URL(url.startsWith('http') ? url : 'https://' + url)
            .hostname.replace(/^www\./, '');
    } catch {
        return url.replace(/^https?:\/\/(www\.)?/, '').split('/')[0];
    }
};

const formatAge = (cachedAt) => {
    if (!cachedAt) return null;
    const diff = Math.floor((Date.now() - new Date(cachedAt)) / (1000 * 60 * 60 * 24));
    if (diff === 0) return 'today';
    if (diff === 1) return '1 day ago';
    return `${diff} days ago`;
};

const KD_COLORS = {
    Easy:    'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    Medium:  'bg-orange-500/20  text-orange-400  border-orange-500/30',
    Hard:    'bg-red-500/20     text-red-400     border-red-500/30',
    Unknown: 'bg-zinc-500/20    text-zinc-400    border-zinc-500/30',
};

const PRIORITY_COLORS = {
    High:   { badge: 'bg-red-500/20 text-red-400 border-red-500/30',          dot: 'bg-red-400' },
    Medium: { badge: 'bg-orange-500/20 text-orange-400 border-orange-500/30', dot: 'bg-orange-400' },
    Low:    { badge: 'bg-zinc-700/40 text-zinc-400 border-zinc-600/30',        dot: 'bg-zinc-500' },
};

// ─── sub-components ───────────────────────────────────────────────────────────

function SectionHeader({ icon, title, subtitle }) {
    return (
        <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center shrink-0">
                {icon}
            </div>
            <div>
                <h3 className="text-base font-semibold text-white">{title}</h3>
                {subtitle && <p className="text-xs text-zinc-500 mt-0.5">{subtitle}</p>}
            </div>
        </div>
    );
}

function PillarBar({ pillar, max }) {
    const pct = Math.min(100, Math.round((pillar.count / max) * 100));
    return (
        <div className="flex items-center gap-3 py-1.5">
            <div className="w-36 shrink-0">
                <p className="text-sm text-zinc-300 truncate capitalize">{pillar.term}</p>
            </div>
            <div className="flex-1 bg-zinc-800/60 rounded-full h-1.5 overflow-hidden">
                <div
                    className="h-full bg-gradient-to-r from-violet-500 to-blue-500 rounded-full transition-all"
                    style={{ width: `${pct}%` }}
                />
            </div>
            <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs text-zinc-500 w-16 text-right">{pillar.count} pages</span>
                <Badge className={`text-xs hover:bg-transparent ${KD_COLORS[pillar.difficulty] || KD_COLORS.Unknown}`}>
                    {pillar.kd !== null && pillar.kd !== undefined ? `KD ${pillar.kd}` : 'KD ?'}
                </Badge>
            </div>
        </div>
    );
}

function ActionCard({ item, index, onGenerate }) {
    const pc = PRIORITY_COLORS[item.priority] || PRIORITY_COLORS.Low;
    return (
        <div className="flex gap-3 p-4 bg-zinc-900/40 border border-white/10 hover:border-white/20 rounded-xl transition-all">
            <div className="flex items-center justify-center w-7 h-7 rounded-full bg-white/5 shrink-0 mt-0.5">
                <span className="text-xs font-bold text-zinc-400">{index + 1}</span>
            </div>
            <div className="flex-1 min-w-0 space-y-1.5">
                <div className="flex items-start gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-white leading-snug flex-1">{item.action}</p>
                    <Badge className={`text-xs hover:bg-transparent shrink-0 ${pc.badge}`}>
                        {item.priority}
                    </Badge>
                </div>
                <p className="text-xs text-zinc-400 leading-relaxed">{item.detail}</p>
                {item.why && (
                    <p className="text-xs text-zinc-600 italic border-l-2 border-zinc-700 pl-2">{item.why}</p>
                )}
            </div>
        </div>
    );
}

function ContentAngleCard({ angle, onWrite }) {
    return (
        <div className="p-4 bg-gradient-to-br from-violet-500/5 to-blue-500/5 border border-violet-500/20 hover:border-violet-500/40 rounded-xl transition-all space-y-2">
            <p className="text-sm font-semibold text-white leading-snug">"{angle.title}"</p>
            <div className="flex items-start gap-1.5">
                <ChevronRightIcon className="w-3 h-3 text-violet-400 mt-0.5 shrink-0" />
                <p className="text-xs text-zinc-400 leading-relaxed">{angle.angle}</p>
            </div>
            {angle.target_keyword && (
                <div className="flex items-center justify-between pt-1">
                    <span className="text-xs text-zinc-500">
                        Target: <span className="text-violet-400 font-medium">{angle.target_keyword}</span>
                    </span>
                    <Button
                        className="h-6 px-2.5 text-xs font-semibold bg-violet-600 hover:bg-violet-700 text-white gap-1 rounded-md"
                        onClick={() => onWrite(angle.title)}
                    >
                        <PlusIcon className="w-3 h-3" />
                        Write
                    </Button>
                </div>
            )}
        </div>
    );
}

// ─── main component ───────────────────────────────────────────────────────────

export default function RankAbove({ user }) {
    const navigate = useNavigate();
    const [yourSiteUrl, setYourSiteUrl]     = useState('');
    const [competitorUrl, setCompetitorUrl] = useState('');
    const [locationCode, setLocationCode] = useState(DEFAULT_LOCATION);
    const [loading, setLoading]           = useState(false);
    const [error, setError]               = useState(null);
    const [report, setReport]             = useState(null);
    const [recent, setRecent]             = useState([]);
    const [activeTab, setActiveTab]       = useState('overview');

    // QuickPost
    const [showQuickPost, setShowQuickPost] = useState(false);
    const [prefilledTopic, setPrefilledTopic] = useState('');

    useEffect(() => { fetchRecent(); }, []);

    const fetchRecent = async () => {
        try {
            const res = await rankAboveAPI.getRecent();
            if (res.data.success) setRecent(res.data.data || []);
        } catch { /* non-critical */ }
    };

    const handleGenerate = async (
        forceRefresh    = false,
        overrideYourUrl = undefined,
        overrideCompUrl = undefined,
        overrideLoc     = undefined
    ) => {
        const y = overrideYourUrl !== undefined ? overrideYourUrl : yourSiteUrl;
        const u = overrideCompUrl !== undefined ? overrideCompUrl : competitorUrl;
        const l = overrideLoc     !== undefined ? overrideLoc     : locationCode;

        if (!y.trim()) { setError('Please enter your website URL.'); return; }
        if (!u.trim()) { setError('Please enter the competitor URL.'); return; }

        const normalise = s => s.trim().startsWith('http') ? s.trim() : 'https://' + s.trim();

        try { new URL(normalise(y)); } catch {
            setError('Please enter a valid URL for your site (e.g. https://myblog.com).');
            return;
        }
        try { new URL(normalise(u)); } catch {
            setError('Please enter a valid competitor URL (e.g. https://healthline.com).');
            return;
        }

        setLoading(true);
        setError(null);
        setReport(null);
        setActiveTab('overview');

        try {
            const res = await rankAboveAPI.generate(normalise(y), normalise(u), l, forceRefresh);
            if (res.data.success) {
                setReport(res.data.data);
                if (overrideYourUrl !== undefined) {
                    setYourSiteUrl(y);
                    setCompetitorUrl(u);
                    setLocationCode(l);
                }
                fetchRecent();
            } else {
                setError(res.data.message || 'Analysis failed. Please try again.');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Analysis failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleRecentPill = (item) => {
        handleGenerate(false, item.niche, item.competitor_url, item.location_code);
        // note: `niche` column stores your_site_url in the DB (same column, repurposed)
    };

    const handleWrite = (topic) => {
        setPrefilledTopic(topic);
        setShowQuickPost(true);
    };

    // ── derived ────────────────────────────────────────────────────────────────
    const strategy          = report?.strategy          || {};
    const pillars           = report?.competitor_pillars || [];
    const maxCount          = pillars.length ? Math.max(...pillars.map(p => p.count)) : 1;

    const TABS = [
        { id: 'overview',  label: 'Overview'       },
        { id: 'pillars',   label: 'Their Focus'     },
        { id: 'plan',      label: 'Your Action Plan'},
        { id: 'angles',    label: 'Content Angles'  },
        { id: 'wins',      label: 'Quick Wins'      },
    ];

    // ── render ─────────────────────────────────────────────────────────────────
    return (
        <div className="text-zinc-50">
                <main className="flex-1 min-h-0 p-6 overflow-y-auto custom-scrollbar">
                    <div className="max-w-4xl mx-auto space-y-6">

                        {/* Header */}
                        <div className="text-center py-4 space-y-2">
                            <h1 className="text-3xl font-extrabold tracking-tight bg-gradient-to-r from-white via-zinc-200 to-zinc-400 bg-clip-text text-transparent">
                                🏆 Rank Above Competitor
                            </h1>
                            <p className="text-zinc-400 text-sm max-w-lg mx-auto">
                                Analyze what your competitor is doing, find their weak spots, and get a step-by-step plan to outrank them.
                            </p>
                        </div>

                        {/* Input card */}
                        <Card className="bg-zinc-900/40 backdrop-blur-xl border-white/10">
                            <CardContent className="pt-6 space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-1.5">
                                        <label className="text-sm font-medium text-zinc-300">Your Website URL</label>
                                        <input
                                            type="text"
                                            className="w-full bg-zinc-800/50 border border-zinc-700 rounded-lg px-4 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none"
                                            placeholder="e.g., https://myblog.com"
                                            value={yourSiteUrl}
                                            onChange={e => setYourSiteUrl(e.target.value)}
                                            onKeyDown={e => e.key === 'Enter' && handleGenerate()}
                                        />
                                        <p className="text-xs text-zinc-600">Your site — we'll scan what you already cover</p>
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-sm font-medium text-zinc-300">Competitor URL</label>
                                        <input
                                            type="text"
                                            className="w-full bg-zinc-800/50 border border-zinc-700 rounded-lg px-4 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none"
                                            placeholder="e.g., https://healthline.com"
                                            value={competitorUrl}
                                            onChange={e => setCompetitorUrl(e.target.value)}
                                            onKeyDown={e => e.key === 'Enter' && handleGenerate()}
                                        />
                                        <p className="text-xs text-zinc-600">The site you want to outrank</p>
                                    </div>
                                </div>

                                <div className="space-y-1.5">
                                    <label className="text-sm font-medium text-zinc-300">Location</label>
                                    <Select value={locationCode} onValueChange={setLocationCode}>
                                        <SelectTrigger className="bg-zinc-800/50 border-zinc-700 text-white">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-zinc-900 border-zinc-700 max-h-60">
                                            {TREND_LOCATIONS.map(loc => (
                                                <SelectItem key={loc.code} value={loc.code} className="text-zinc-300 focus:bg-zinc-800 focus:text-white">
                                                    {loc.flag} {loc.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <Button
                                    onClick={() => handleGenerate()}
                                    disabled={loading}
                                    className="w-full bg-violet-600 hover:bg-violet-700 gap-2 text-white"
                                >
                                    {loading ? (
                                        <><ReloadIcon className="w-4 h-4 animate-spin" /> Analyzing competitor...</>
                                    ) : (
                                        <><TargetIcon className="w-4 h-4" /> Generate Strategy Report</>
                                    )}
                                </Button>

                                {error && (
                                    <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-300 text-sm">
                                        <ExclamationTriangleIcon className="w-4 h-4 mt-0.5 shrink-0" />
                                        {error}
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Recent pills */}
                        {recent.length > 0 && !report && !loading && (
                            <div className="space-y-2">
                                <p className="text-xs text-zinc-500 font-medium flex items-center gap-1.5">
                                    <ClockIcon className="w-3 h-3" /> Recent reports
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {recent.map((item, i) => (
                                        <Badge
                                            key={i}
                                            onClick={() => handleRecentPill(item)}
                                            className="cursor-pointer bg-zinc-800/60 hover:bg-zinc-700/80 text-zinc-300 border border-zinc-700/50 hover:border-zinc-500/50 transition-all font-medium px-3 py-1"
                                        >
                                            {item.niche} <span className="text-zinc-600 mx-1">vs</span> {stripDomain(item.competitor_url)}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Loading state */}
                        {loading && (
                            <div className="py-16 flex flex-col items-center gap-5">
                                <div className="relative">
                                    <div className="w-16 h-16 border-4 border-white/5 rounded-full" />
                                    <div className="absolute inset-0 w-16 h-16 border-4 border-t-violet-500 rounded-full animate-spin" />
                                </div>
                                <div className="text-center space-y-1">
                                    <p className="text-white font-medium">Building your strategy report...</p>
                                    <p className="text-zinc-500 text-sm">Crawling sitemap · fetching KD data · generating AI strategy</p>
                                    <p className="text-zinc-600 text-xs">First run takes 30–60 seconds. Cached forever after that.</p>
                                </div>
                            </div>
                        )}

                        {/* Empty state */}
                        {!report && !loading && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {[
                                    { icon: <TargetIcon className="w-5 h-5 text-violet-400" />, color: 'from-violet-500/20 to-purple-500/20', title: 'Competitor Intelligence', desc: "We crawl their entire site to map out what they're focusing on and where they're weak." },
                                    { icon: <BarChartIcon className="w-5 h-5 text-blue-400" />, color: 'from-blue-500/20 to-cyan-500/20', title: 'KD-backed Keyword Gaps', desc: 'DataForSEO keyword difficulty scores on every topic — so you know exactly which battles are worth fighting.' },
                                    { icon: <RocketIcon className="w-5 h-5 text-emerald-400" />, color: 'from-emerald-500/20 to-green-500/20', title: 'AI Strategy Report', desc: 'Get a prioritized action plan written by AI — specific angles, keywords, and content formats to outrank them.' },
                                ].map((item, i) => (
                                    <div key={i} className="bg-zinc-900/40 border border-white/10 rounded-xl p-4">
                                        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${item.color} flex items-center justify-center mb-3`}>
                                            {item.icon}
                                        </div>
                                        <h3 className="text-sm font-semibold text-white mb-1">{item.title}</h3>
                                        <p className="text-xs text-zinc-400 leading-relaxed">{item.desc}</p>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* ── RESULTS ───────────────────────────────────────────────────────── */}
                        {report && !loading && (
                            <div className="space-y-5">

                                {/* Cache + header bar */}
                                <div className="flex items-center justify-between p-3 bg-zinc-900/60 border border-white/10 rounded-xl flex-wrap gap-2">
                                    <div>
                                        <p className="text-xs text-zinc-500 uppercase tracking-wider font-medium">Report</p>
                                        <p className="text-white font-semibold">
                                            <span className="text-emerald-400">{report.your_domain || stripDomain(report.your_site_url)}</span>
                                            <span className="text-zinc-500 font-normal mx-2">vs</span>
                                            <span className="text-violet-400">{report.competitor_domain || stripDomain(report.competitor_url)}</span>
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {report.is_fresh ? (
                                            <span className="flex items-center gap-1.5 text-emerald-400 text-xs bg-emerald-500/10 border border-emerald-500/20 rounded-full px-2.5 py-1">
                                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Live data
                                            </span>
                                        ) : (
                                            <span className="flex items-center gap-1.5 text-zinc-400 text-xs bg-zinc-800/60 border border-zinc-700/50 rounded-full px-2.5 py-1">
                                                <ClockIcon className="w-3 h-3" /> Cached · {formatAge(report.cached_at)}
                                            </span>
                                        )}
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-7 text-xs text-zinc-400 hover:text-white border border-zinc-700/50 hover:border-zinc-500 gap-1.5"
                                            onClick={() => handleGenerate(true)}
                                        >
                                            <ReloadIcon className="w-3 h-3" /> Refresh
                                        </Button>
                                    </div>
                                </div>

                                {/* Stats strip */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    {[
                                        { label: 'Your pages',         value: report.your_crawled ? (report.your_stats?.total_pages?.toLocaleString() ?? '—') : 'Not crawled', color: 'text-emerald-400' },
                                        { label: 'Competitor pages',   value: report.competitor_stats?.total_pages?.toLocaleString() ?? '—',   color: 'text-white'      },
                                        { label: 'Content gaps found', value: report.gaps?.length ?? '—',                                        color: 'text-amber-400'  },
                                        { label: 'Easy wins (KD≤40)',  value: report.easy_wins?.length ?? '—',                                   color: 'text-violet-400' },
                                    ].map((s, i) => (
                                        <div key={i} className="p-3 bg-zinc-900/40 border border-white/10 rounded-xl">
                                            <p className="text-xs text-zinc-500">{s.label}</p>
                                            <p className={`text-xl font-bold mt-0.5 ${s.color}`}>{s.value}</p>
                                        </div>
                                    ))}
                                </div>

                                {/* Tab bar */}
                                <div className="flex gap-1 p-1 bg-zinc-900/60 border border-white/10 rounded-xl overflow-x-auto">
                                    {TABS.map(tab => (
                                        <button
                                            key={tab.id}
                                            onClick={() => setActiveTab(tab.id)}
                                            className={`flex-1 min-w-max px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
                                                activeTab === tab.id
                                                    ? 'bg-violet-600 text-white'
                                                    : 'text-zinc-400 hover:text-zinc-200 hover:bg-white/5'
                                            }`}
                                        >
                                            {tab.label}
                                        </button>
                                    ))}
                                </div>

                                {/* ── TAB: OVERVIEW ──────────────────────────────────────────────── */}
                                {activeTab === 'overview' && (
                                    <div className="space-y-4">

                                        {/* Competitor summary + your position */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <Card className="bg-zinc-900/40 border-white/10">
                                                <CardContent className="pt-5">
                                                    <SectionHeader
                                                        icon={<TargetIcon className="w-4 h-4 text-violet-400" />}
                                                        title={`What ${report.competitor_domain || stripDomain(report.competitor_url)} is doing`}
                                                    />
                                                    <p className="text-sm text-zinc-300 leading-relaxed">
                                                        {strategy.competitor_summary || 'No summary available.'}
                                                    </p>
                                                </CardContent>
                                            </Card>
                                            <Card className="bg-zinc-900/40 border-white/10">
                                                <CardContent className="pt-5">
                                                    <SectionHeader
                                                        icon={<RocketIcon className="w-4 h-4 text-emerald-400" />}
                                                        title={`Where ${report.your_domain || stripDomain(report.your_site_url)} stands`}
                                                    />
                                                    <p className="text-sm text-zinc-300 leading-relaxed">
                                                        {strategy.your_position || (report.your_crawled ? 'Position analysis not available.' : 'Your sitemap could not be crawled — install a sitemap plugin or make it publicly accessible for a personalized comparison.')}
                                                    </p>
                                                </CardContent>
                                            </Card>
                                        </div>

                                        {/* Strengths & Weaknesses */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <Card className="bg-zinc-900/40 border-white/10">
                                                <CardContent className="pt-5">
                                                    <SectionHeader
                                                        icon={<CheckCircledIcon className="w-4 h-4 text-emerald-400" />}
                                                        title="Their Strengths"
                                                        subtitle="What they do well — you need to match or exceed"
                                                    />
                                                    <ul className="space-y-2">
                                                        {(strategy.competitor_strengths || []).map((s, i) => (
                                                            <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                                                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-1.5 shrink-0" />
                                                                {s}
                                                            </li>
                                                        ))}
                                                        {!strategy.competitor_strengths?.length && (
                                                            <li className="text-sm text-zinc-500 italic">None identified</li>
                                                        )}
                                                    </ul>
                                                </CardContent>
                                            </Card>

                                            <Card className="bg-zinc-900/40 border-white/10">
                                                <CardContent className="pt-5">
                                                    <SectionHeader
                                                        icon={<CrossCircledIcon className="w-4 h-4 text-red-400" />}
                                                        title="Their Weaknesses"
                                                        subtitle="Gaps you can exploit to outrank them"
                                                    />
                                                    <ul className="space-y-2">
                                                        {(strategy.competitor_weaknesses || []).map((w, i) => (
                                                            <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                                                                <span className="w-1.5 h-1.5 rounded-full bg-red-400 mt-1.5 shrink-0" />
                                                                {w}
                                                            </li>
                                                        ))}
                                                        {!strategy.competitor_weaknesses?.length && (
                                                            <li className="text-sm text-zinc-500 italic">None identified</li>
                                                        )}
                                                    </ul>
                                                </CardContent>
                                            </Card>
                                        </div>

                                        {/* Easy wins preview */}
                                        {(report.easy_wins || []).length > 0 && (
                                            <Card className="bg-gradient-to-br from-emerald-500/5 to-green-500/5 border-emerald-500/20">
                                                <CardContent className="pt-5">
                                                    <SectionHeader
                                                        icon={<LightningBoltIcon className="w-4 h-4 text-emerald-400" />}
                                                        title="Easy-Win Keywords"
                                                        subtitle="Low KD (≤40) related keywords the competitor barely covers"
                                                    />
                                                    <div className="flex flex-wrap gap-2">
                                                        {report.easy_wins.map((w, i) => (
                                                            <div
                                                                key={i}
                                                                className="flex items-center gap-2 px-3 py-1.5 bg-zinc-800/60 border border-zinc-700/50 rounded-lg"
                                                            >
                                                                <span className="text-sm text-zinc-200">{w.keyword}</span>
                                                                <Badge className={`text-xs hover:bg-transparent ${KD_COLORS[w.difficulty]}`}>
                                                                    KD {w.kd}
                                                                </Badge>
                                                                <Button
                                                                    className="h-5 px-2 text-xs bg-emerald-600 hover:bg-emerald-700 text-white gap-0.5 rounded"
                                                                    onClick={() => handleWrite(w.keyword)}
                                                                >
                                                                    <PlusIcon className="w-2.5 h-2.5" />
                                                                    Write
                                                                </Button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        )}
                                    </div>
                                )}

                                {/* ── TAB: PILLARS ───────────────────────────────────────────────── */}
                                {activeTab === 'pillars' && (
                                    <Card className="bg-zinc-900/40 border-white/10">
                                        <CardContent className="pt-5">
                                            <SectionHeader
                                                icon={<BarChartIcon className="w-4 h-4 text-blue-400" />}
                                                title={`${report.competitor_domain || stripDomain(report.competitor_url)}'s Content Focus`}
                                                subtitle={`Top topics by page count across ${report.competitor_stats?.total_pages?.toLocaleString()} indexed pages`}
                                            />
                                            <div className="space-y-1">
                                                {pillars.map((p, i) => (
                                                    <PillarBar key={i} pillar={p} max={maxCount} />
                                                ))}
                                                {!pillars.length && (
                                                    <p className="text-sm text-zinc-500 italic">No pillar data available.</p>
                                                )}
                                            </div>
                                        </CardContent>
                                    </Card>
                                )}

                                {/* ── TAB: ACTION PLAN ───────────────────────────────────────────── */}
                                {activeTab === 'plan' && (
                                    <div className="space-y-4">
                                        <p className="text-sm text-zinc-400">
                                            Prioritized steps for <span className="text-emerald-400">{report.your_domain || stripDomain(report.your_site_url)}</span> to outrank <span className="text-violet-400">{report.competitor_domain || stripDomain(report.competitor_url)}</span>
                                        </p>
                                        <div className="space-y-3">
                                            {(strategy.your_action_plan || []).map((item, i) => (
                                                <ActionCard key={i} item={item} index={i} onGenerate={handleWrite} />
                                            ))}
                                            {!strategy.your_action_plan?.length && (
                                                <p className="text-sm text-zinc-500 italic py-4 text-center">No action plan generated. Try refreshing the report.</p>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* ── TAB: CONTENT ANGLES ────────────────────────────────────────── */}
                                {activeTab === 'angles' && (
                                    <div className="space-y-4">
                                        <p className="text-sm text-zinc-400">
                                            Specific article ideas targeting gaps <span className="text-violet-400">{report.competitor_domain || stripDomain(report.competitor_url)}</span> has missed — ready to write.
                                        </p>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {(strategy.content_angles || []).map((angle, i) => (
                                                <ContentAngleCard key={i} angle={angle} onWrite={handleWrite} />
                                            ))}
                                            {!strategy.content_angles?.length && (
                                                <p className="text-sm text-zinc-500 italic">No content angles generated.</p>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* ── TAB: QUICK WINS ────────────────────────────────────────────── */}
                                {activeTab === 'wins' && (
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <Card className="bg-zinc-900/40 border-white/10">
                                                <CardContent className="pt-5">
                                                    <SectionHeader
                                                        icon={<StarIcon className="w-4 h-4 text-amber-400" />}
                                                        title="Quick Wins"
                                                        subtitle="Do these first — highest ROI with lowest effort"
                                                    />
                                                    <ul className="space-y-2.5">
                                                        {(strategy.quick_wins || []).map((w, i) => (
                                                            <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                                                                <LightningBoltIcon className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" />
                                                                {w}
                                                            </li>
                                                        ))}
                                                        {!strategy.quick_wins?.length && (
                                                            <li className="text-sm text-zinc-500 italic">None identified</li>
                                                        )}
                                                    </ul>
                                                </CardContent>
                                            </Card>

                                            <Card className="bg-zinc-900/40 border-white/10">
                                                <CardContent className="pt-5">
                                                    <SectionHeader
                                                        icon={<RocketIcon className="w-4 h-4 text-violet-400" />}
                                                        title="Long-Term Plays"
                                                        subtitle="Sustainable strategies that compound over time"
                                                    />
                                                    <ul className="space-y-2.5">
                                                        {(strategy.long_term_plays || []).map((p, i) => (
                                                            <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                                                                <ArrowUpIcon className="w-3.5 h-3.5 text-violet-400 mt-0.5 shrink-0" />
                                                                {p}
                                                            </li>
                                                        ))}
                                                        {!strategy.long_term_plays?.length && (
                                                            <li className="text-sm text-zinc-500 italic">None identified</li>
                                                        )}
                                                    </ul>
                                                </CardContent>
                                            </Card>
                                        </div>

                                        {/* Write top angle CTA */}
                                        {(strategy.content_angles || []).length > 0 && (
                                            <div className="flex gap-3 pt-2">
                                                <Button
                                                    className="flex-1 bg-violet-600 hover:bg-violet-700 gap-2 text-white"
                                                    onClick={() => handleWrite(strategy.content_angles[0]?.title || report.niche)}
                                                >
                                                    <PlusIcon className="w-4 h-4" />
                                                    Write Top Article Opportunity
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    className="gap-2"
                                                    onClick={() => { setReport(null); setYourSiteUrl(''); setCompetitorUrl(''); }}
                                                >
                                                    <MagnifyingGlassIcon className="w-4 h-4" />
                                                    New Analysis
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                )}

                            </div>
                        )}

                    </div>
                </main>

            {showQuickPost && (
                <QuickPost
                    user={user}
                    prefilledTopic={prefilledTopic}
                    onClose={(reason) => {
                        setShowQuickPost(false);
                        if (reason === 'topics') navigate('/topic-queue');
                    }}
                />
            )}
        </div>
    );
}
